import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Notifications,
  ShoppingCart,
  Inventory,
  Payment,
  SystemUpdate,
  Star,
  Delete,
  CheckCircle,
  Warning,
  Info,
  NotificationsActive,
  NotificationsOff
} from '@mui/icons-material';
import { useNotifications } from '../../context/NotificationContext';
import pushNotificationService from '../../services/pushNotificationService';

const NotificationDropdown = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification, loadNotifications } = useNotifications();
  
  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushError, setPushError] = useState(null);
  const [showPushAlert, setShowPushAlert] = useState(false);
  

  // Check push notification support and status on component mount
  useEffect(() => {
    const checkPushStatus = async () => {
      try {
        const isSupported = pushNotificationService.isPushSupported();
        setPushSupported(isSupported);
        
        if (isSupported) {
          const status = await pushNotificationService.getSubscriptionStatus();
          setPushPermission(status.permission);
          setPushSubscribed(status.subscribed);
        }
      } catch (error) {
        console.error('Error checking push status:', error);
        setPushError(error.message);
      }
    };

    checkPushStatus();
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Load notifications when dropdown is opened
    loadNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
  };

  // Push notification handlers
  const handleEnablePushNotifications = async () => {
    try {
      setPushError(null);
      await pushNotificationService.initialize();
      setPushPermission('granted');
      setPushSubscribed(true);
      setShowPushAlert(true);
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      setPushError(error.message);
      setShowPushAlert(true);
    }
  };


  const handleDisablePushNotifications = async () => {
    try {
      setPushError(null);
      await pushNotificationService.unsubscribe();
      setPushSubscribed(false);
      setShowPushAlert(true);
    } catch (error) {
      console.error('Error disabling push notifications:', error);
      setPushError(error.message);
      setShowPushAlert(true);
    }
  };


  const getNotificationIcon = (type, isRead) => {
    // For unread notifications, use white icons; for read notifications, use colored icons
    const iconColor = isRead ? {
      order: '#DC143C',
      inventory: '#FF9800', 
      payment: '#4CAF50',
      system: '#2196F3',
      review: '#FFC107',
      default: '#9E9E9E'
    } : {
      order: '#ffffff',
      inventory: '#ffffff',
      payment: '#ffffff', 
      system: '#ffffff',
      review: '#ffffff',
      default: '#ffffff'
    };

    switch (type) {
      case 'order':
        return <ShoppingCart sx={{ color: iconColor.order }} />;
      case 'inventory':
        return <Inventory sx={{ color: iconColor.inventory }} />;
      case 'payment':
        return <Payment sx={{ color: iconColor.payment }} />;
      case 'system':
        return <SystemUpdate sx={{ color: iconColor.system }} />;
      case 'review':
        return <Star sx={{ color: iconColor.review }} />;
      default:
        return <Info sx={{ color: iconColor.default }} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#F44336';
      case 'medium':
        return '#FF9800';
      case 'low':
        return '#4CAF50';
      default:
        return '#9E9E9E';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <Warning fontSize="small" />;
      case 'medium':
        return <Info fontSize="small" />;
      case 'low':
        return <CheckCircle fontSize="small" />;
      default:
        return <Info fontSize="small" />;
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ position: 'relative' }}
      >
        <Badge 
          badgeContent={unreadCount > 0 ? unreadCount : null} 
          color="error"
          max={99}
        >
          <Notifications />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            mt: 1
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={markAllAsRead}
                sx={{ color: '#DC143C', fontSize: '0.75rem' }}
              >
                Mark all as read
              </Button>
            )}
          </Box>
          {unreadCount > 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        <List sx={{ p: 0 }}>
          {notifications.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="No notifications"
                secondary="You're all caught up!"
                sx={{ textAlign: 'center' }}
              />
            </ListItem>
          ) : (
            notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    backgroundColor: notification.read ? 'transparent' : '#FFF0F3',
                    '&:hover': {
                      backgroundColor: notification.read ? '#f5f5f5' : '#FFE6E9'
                    },
                    cursor: 'pointer'
                  }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <ListItemIcon>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: notification.read ? '#e0e0e0' : '#DC143C',
                        color: notification.read ? '#666666' : '#ffffff'
                      }}
                    >
                      {getNotificationIcon(notification.type, notification.read)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: notification.read ? 'normal' : 'bold',
                            color: notification.read ? 'text.secondary' : 'text.primary'
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Chip
                          icon={getPriorityIcon(notification.priority)}
                          label={notification.priority}
                          size="small"
                          sx={{
                            backgroundColor: getPriorityColor(notification.priority),
                            color: 'white',
                            fontSize: '0.6rem',
                            height: 20
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: notification.read ? 'text.secondary' : 'text.primary',
                            mb: 0.5
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {notification.time}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                      sx={{ color: '#999' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </List>

                {/* Push Notification Controls */}
                {pushSupported && (
                  <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#DC143C' }}>
                      Desktop Notifications
                    </Typography>
                    
                    
                    {!pushSubscribed && (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleEnablePushNotifications}
                        startIcon={<NotificationsActive />}
                        sx={{ 
                          backgroundColor: '#DC143C', 
                          color: 'white',
                          mb: 1,
                          '&:hover': { backgroundColor: '#B71C1C' }
                        }}
                      >
                        Enable Desktop Notifications
                      </Button>
                    )}
                    
                    {pushPermission === 'granted' && pushSubscribed && (
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleDisablePushNotifications}
                        startIcon={<NotificationsOff />}
                        sx={{ 
                          borderColor: '#FF9800', 
                          color: '#FF9800'
                        }}
                      >
                        Disable Desktop Notifications
                      </Button>
                    )}
                    
                    {pushPermission === 'denied' && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                        Desktop notifications are blocked. Please enable them in your browser settings.
                      </Typography>
                    )}
                  </Box>
                )}

                {notifications.length > 0 && (
                  <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleClose}
                      sx={{ borderColor: '#DC143C', color: '#DC143C' }}
                    >
                      View All Notifications
                    </Button>
                  </Box>
                )}
      </Menu>

      {/* Error Alert */}
      <Snackbar
        open={showPushAlert}
        autoHideDuration={6000}
        onClose={() => setShowPushAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowPushAlert(false)}
          severity={pushError ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {pushError || 'Push notification action completed successfully!'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationDropdown; 